import React, { memo, useCallback, useMemo, useState } from 'react';
import { Box, Button, Flex, Image, Stack, Tabs, Text } from '@chakra-ui/react';
import { Plus } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

import emptyInterviewImage from '@/assets/image/empty-job-cover.svg';
import { useVideoPlayer } from '@/hooks/useVideoPlayer';
import { Screening } from '@/types/Screening';
import { InterviewAnalysisSection, ScreeningStatus, AgencyPlan, AgencyUsageType } from '@/types/enums';
import {
  ScreeningPlaybackSection,
  ScreeningQNAsSection,
  ScreeningSummarySection
} from '@/views/InterviewAnalysis';
import { LimitExhaustedDialog } from '@/views/Interview/Agent/LimitExhaustedDialog';
import { fetchAPI } from '@/api/fetch';
import { apiGetAccountProfile } from '@/api/services/account.service';

type Section = {
  label: string;
  value: InterviewAnalysisSection;
};

const SCREENING_SECTIONS: Section[] = [
  {
    label: 'Summary',
    value: InterviewAnalysisSection.Summary
  },
  {
    label: 'Q&As',
    value: InterviewAnalysisSection.QNAs
  },
  {
    label: 'Playback',
    value: InterviewAnalysisSection.Playback
  }
];

const EMPTY_STATE_CONFIG = {
  image: {
    width: '250px',
    height: '250px',
    alt: 'Empty Interview'
  },
  title: 'No interviews yet',
  description:
    'There are no interviews associated with this candidate yet. Once interviews are conducted, the analysis will be available here.',
  buttonText: 'Create session'
} as const;

const IN_PROGRESS_CONFIG = {
  title: 'Interview in progress',
  description:
    'The interview is still in progress. Please check back later for the analysis once the interview is completed.'
} as const;

interface InterviewAnalysisIndexProps {
  candidateId: string;
  screenings: Screening[];
}

const InterviewAnalysisIndex = memo(({ candidateId, screenings }: InterviewAnalysisIndexProps) => {
  const navigate = useNavigate();
  const [selectedScreeningIndex, setSelectedScreeningIndex] = useState<number>(0);
  const [isLimitDialogOpen, setIsLimitDialogOpen] = useState(false);
  const [isFreePlan, setIsFreePlan] = useState(false);

  const handleAddSession = useCallback(async () => {
    // Fetch fresh account data to check latest limits
    try {
      const response = await fetchAPI<Account>({
        request: () => apiGetAccountProfile()
      });

      if (response.success && response.data) {
        const freshAccount = response.data;
        const agenticInterviewsUsage = freshAccount.agency?.usages?.find(
          (usage) => usage.type === AgencyUsageType.AgenticInterviews
        );
        const isExhausted = agenticInterviewsUsage?.exhausted || false;
        const isPlanFree = freshAccount.agency?.plan === AgencyPlan.FREE;

        if (isExhausted || isPlanFree) {
          setIsFreePlan(isPlanFree);
          setIsLimitDialogOpen(true);
        } else {
          navigate(`/interview/agent/create/${candidateId}`);
        }
      } else {
        // If API call fails, allow navigation
        navigate(`/interview/agent/create/${candidateId}`);
      }
    } catch {
      // If error occurs, allow navigation
      navigate(`/interview/agent/create/${candidateId}`);
    }
  }, [candidateId, navigate]);

  const addSessionButton = useMemo(
    () => (
      <Button
        border="1px solid"
        borderRadius="8px"
        borderColor="primary.900"
        bg="neutrals.0"
        color="primary.900"
        fontSize="16px"
        p="8px 12px"
        _hover={{ bg: 'primary.900', color: 'neutrals.0' }}
        onClick={handleAddSession}
        aria-label="Add new interview session"
      >
        <Flex alignItems="center" gap="8px">
          <Plus aria-hidden="true" />
          <Text>Add session</Text>
        </Flex>
      </Button>
    ),
    [handleAddSession]
  );

  let content;

  if (screenings.length === 0) {
    content = (
      <Stack
        justifyContent="center"
        alignItems="center"
        py="60px"
        w="100%"
        gap="12px"
        role="region"
        aria-label="No interviews available"
      >
        <Image
          w={EMPTY_STATE_CONFIG.image.width}
          h={EMPTY_STATE_CONFIG.image.height}
          src={emptyInterviewImage}
          alt={EMPTY_STATE_CONFIG.image.alt}
        />
        <Text fontSize="20px" fontWeight="semibold" color="neutrals.600">
          {EMPTY_STATE_CONFIG.title}
        </Text>
        <Text fontSize="16px" color="neutrals.500" textAlign="center" w="500px">
          {EMPTY_STATE_CONFIG.description}
        </Text>
        <Button
          borderRadius="8px"
          bg="primary.900"
          color="neutral.0"
          fontSize="16px"
          p="10px 24px"
          _hover={{ bg: 'primary.700' }}
          onClick={handleAddSession}
          aria-label="Create new interview session"
        >
          {EMPTY_STATE_CONFIG.buttonText}
        </Button>
      </Stack>
    );
  } else if (screenings.length === 1) {
    const screening = screenings[0];

    content = (
      <Stack gap="16px">
        <Box flex="1" display="flex" justifyContent="flex-end">
          {addSessionButton}
        </Box>
        {screening.status === ScreeningStatus.AnalysisCompleted ? (
          <InterviewAnalysis screening={screening} />
        ) : (
          <InProgressInterview screening={screening} />
        )}
      </Stack>
    );
  } else {
    content = (
      <Tabs.Root
        value={selectedScreeningIndex.toString()}
        onValueChange={(e) => setSelectedScreeningIndex(parseInt(e.value))}
        variant="enclosed"
      >
        <Flex mb="24px" w="100%" alignItems="center" gap="16px">
          <Box flex="1" />
          <Box border="none" boxShadow="none" flex="0">
            <Tabs.List
              aria-label="Interview Tabs"
              overflowX="auto"
              whiteSpace="nowrap"
              border="none"
              boxShadow="none"
              display="flex"
              justifyContent="center"
            >
              {screenings.map((_, index) => (
                <Tabs.Trigger key={index} value={index.toString()} asChild>
                  <Text
                    fontSize="16px"
                    fontWeight={selectedScreeningIndex === index ? 'semibold' : 'medium'}
                    color={selectedScreeningIndex === index ? 'primary.900' : 'neutrals.500'}
                  >
                    Interview {index + 1}
                  </Text>
                </Tabs.Trigger>
              ))}
            </Tabs.List>
          </Box>
          <Box flex="1" display="flex" justifyContent="flex-end">
            {addSessionButton}
          </Box>
        </Flex>
        {screenings.map((screening, index) => (
          <Tabs.Content key={`screening-${index}`} value={index.toString()}>
            {screening.status === ScreeningStatus.AnalysisCompleted ? (
              <InterviewAnalysis screening={screening} />
            ) : (
              <InProgressInterview screening={screening} />
            )}
          </Tabs.Content>
        ))}
      </Tabs.Root>
    );
  }

  return (
    <>
      {content}
      <LimitExhaustedDialog
        isOpen={isLimitDialogOpen}
        onClose={() => setIsLimitDialogOpen(false)}
        isFreePlan={isFreePlan}
        usageType="agentic-interview"
      />
    </>
  );
});

InterviewAnalysisIndex.displayName = 'InterviewAnalysisIndex';

export default InterviewAnalysisIndex;

const InProgressInterview = memo(({ screening }: { screening: Screening }) => {
  return (
    <Flex
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      py="60px"
      w="100%"
    >
      <Text fontSize="20px" fontWeight="bold" color="neutrals.900" mb="8px" textAlign="center">
        {IN_PROGRESS_CONFIG.title}
      </Text>
      <Text fontSize="16px" color="neutrals.500" textAlign="center" w="500px">
        {IN_PROGRESS_CONFIG.description}
      </Text>
      {screening.deadline && (
        <Text fontSize="16px" color="neutrals.700" mt="16px">
          <strong>Deadline:</strong> {new Date(screening.deadline * 1000).toLocaleString()}
        </Text>
      )}
    </Flex>
  );
});

InProgressInterview.displayName = 'InProgressInterview';

const InterviewAnalysis = memo(({ screening }: { screening: Screening }) => {
  const [section, setSection] = useState<InterviewAnalysisSection>(
    InterviewAnalysisSection.Summary
  );
  const [selectedScreeningQuestionIndex, setSelectedScreeningQuestionIndex] = useState<number>(0);

  const screeningVideoPlayer = useVideoPlayer();

  const handleSectionChange = useCallback((newSection: InterviewAnalysisSection) => {
    setSection(newSection);
  }, []);

  const handleQuestionClick = useCallback(
    (index: number) => {
      setSelectedScreeningQuestionIndex(index);
      if (screeningVideoPlayer.playerRef.current) {
        screeningVideoPlayer.playerRef.current.seekTo(0);
      }
    },
    [screeningVideoPlayer.playerRef]
  );

  return (
    <Flex gap="24px">
      <Stack>
        <Box
          w="240px"
          minW="240px"
          h="fit-content"
          border="1px solid"
          borderColor="neutrals.200"
          borderRadius="12px"
          p="16px 12px"
          role="navigation"
          aria-label="Interview analysis sections"
        >
          <Stack>
            {SCREENING_SECTIONS.map((item) => (
              <Box
                key={item.value}
                p="16px 24px"
                borderRadius="12px"
                bg={item.value === section ? 'neutrals.100' : 'transparent'}
                cursor="pointer"
                onClick={() => handleSectionChange(item.value)}
                role="button"
                tabIndex={0}
                aria-pressed={item.value === section}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSectionChange(item.value);
                  }
                }}
              >
                <Text
                  color="neutrals.700"
                  fontSize="14px"
                  fontWeight={item.value === section ? 'semibold' : 'normal'}
                >
                  {item.label}
                </Text>
              </Box>
            ))}
          </Stack>
        </Box>
        {section == InterviewAnalysisSection.Playback && (
          <Box w="240px" minW="240px" mt="32px" maxH="300px" overflowY="auto">
            <Stack gap="0">
              {screening.questions.map((_, index) => (
                <React.Fragment key={index}>
                  <Box
                    p="4px 14px"
                    cursor="pointer"
                    borderRadius="8px"
                    onClick={() => handleQuestionClick(index)}
                    _hover={{
                      color: 'neutrals.500'
                    }}
                  >
                    <Flex alignItems="center" gap="8px">
                      <Box
                        w="8px"
                        h="8px"
                        p="0"
                        borderRadius="50%"
                        bg={
                          selectedScreeningQuestionIndex === index ? 'primary.900' : 'neutrals.300'
                        }
                      />
                      <Text
                        fontSize="14px"
                        color={
                          selectedScreeningQuestionIndex === index ? 'neutrals.900' : 'neutrals.600'
                        }
                        fontWeight={
                          selectedScreeningQuestionIndex === index ? 'semibold' : 'normal'
                        }
                      >
                        Question {index + 1}
                      </Text>
                    </Flex>
                  </Box>
                  {index < screening.questions.length - 1 && (
                    <Box
                      ml="16px"
                      height="20px"
                      borderLeft="2px solid"
                      borderColor="neutrals.200"
                      alignSelf="flex-start"
                    />
                  )}
                </React.Fragment>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
      {section === InterviewAnalysisSection.Summary && (
        <ScreeningSummarySection
          screeningType={screening.screening_type}
          analysisResult={screening.analysis_result}
        />
      )}
      {section === InterviewAnalysisSection.QNAs && <ScreeningQNAsSection screening={screening} />}
      {section === InterviewAnalysisSection.Playback && (
        <ScreeningPlaybackSection
          screening={screening}
          selectedQuestionIndex={selectedScreeningQuestionIndex}
          player={screeningVideoPlayer}
        />
      )}
    </Flex>
  );
});

InterviewAnalysis.displayName = 'InterviewAnalysis';
